import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">{question}</span>
        {open ? (
          <i className="ri-arrow-up-s-line text-foreground-500 transition-transform duration-300 text-lg flex-shrink-0"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 text-lg flex-shrink-0"></i>
        )}
      </button>
      <div
        className={`transition-all duration-300 overflow-hidden ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const featuredArticles = articles.slice(0, 6);

  const getCatColor = (color: string) => {
    switch (color) {
      case 'primary': return { bg: 'bg-primary-100', text: 'text-primary-700', icon: 'text-primary-500' };
      case 'accent': return { bg: 'bg-accent-100', text: 'text-accent-700', icon: 'text-accent-500' };
      case 'secondary': return { bg: 'bg-secondary-100', text: 'text-secondary-700', icon: 'text-secondary-500' };
      default: return { bg: 'bg-background-100', text: 'text-foreground-700', icon: 'text-foreground-500' };
    }
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
  };

  return (
    <>
      <SEOHead
        title={`${siteInfo.name} - ${siteInfo.tagline}`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
        ogType="website"
        ogImage="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20illustration%20symbolizing%20marriage%20trust%20and%20healthy%20boundaries%20between%20a%20couple%2C%20soft%20beige%20and%20cream%20tones%2C%20clean%20editorial%20style&width=1200&height=630&seq=home-og-01&orientation=landscape"
      />

      <main>
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20artistic%20illustration%20of%20a%20couple%20walking%20together%20in%20a%20sunlit%20autumn%20park%20with%20soft%20golden%20light%20filtering%20through%20trees%2C%20gentle%20atmosphere%2C%20clean%20composition%2C%20beige%20and%20cream%20color%20palette%2C%20editorial%20style%2C%20conveying%20trust%20and%20connection&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
              alt="婚姻边界指南"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-5 leading-tight">
                {siteInfo.tagline}
              </h1>
              <p className="text-base md:text-lg text-background-50/85 leading-relaxed mb-8 max-w-2xl">
                在婚姻的旅途中，社交边界是最容易被忽视却最需要守护的风景线。我们用专业的内容和真诚的分享，帮助每一位已婚人士建立健康、得体的社交边界。
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/category/hun-yin-bian-jie-gan"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  开始探索
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-background-50/15 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-background-50/25 transition-colors cursor-pointer backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative -mt-12 z-20 max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 rounded-lg border border-background-200/70 grid grid-cols-2 md:grid-cols-4 divide-x divide-background-200/70">
            {siteInfo.stats.map((stat, i) => (
              <div key={i} className="py-5 md:py-6 text-center">
                <p className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-10 md:pb-14">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">内容分类</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              从多个维度拆解婚姻社交边界，找到你关心的那个话题
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => {
              const color = getCatColor(cat.color);
              return (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 transition-all duration-300 hover:border-background-300/60 cursor-pointer"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${color.bg} mb-4`}>
                    <i className={`${cat.icon} text-lg ${color.icon}`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {cat.description}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-primary-500">
                    <span>浏览文章</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Featured Articles */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">精选文章</h2>
                <p className="text-sm md:text-base text-foreground-600">深度好文，帮你建立健康的婚姻社交观</p>
              </div>
              <Link
                to="/category/hun-yin-bian-jie-gan"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/hun-yin-bian-jie-gan"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm md:text-base text-foreground-600">
                关于婚姻社交边界，你可能关心的几个问题
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {siteInfo.faqs.slice(0, 5).map((faq, i) => (
                <FAQItem key={i} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20abstract%20background%20with%20soft%20gradient%20of%20beige%20and%20cream%20tones%2C%20gentle%20flowing%20shapes%2C%20calm%20and%20trusting%20atmosphere%2C%20editorial%20style&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-foreground-950/70"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4">
              守护婚姻的边界，从今天开始
            </h2>
            <p className="text-sm md:text-base text-background-50/75 max-w-xl mx-auto mb-8">
              每一段幸福的婚姻都值得被认真守护。加入我们的读者社区，学习如何更好地经营你的婚姻关系。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              了解更多关于我们
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
    </>
  );
}